import {
  Component,
  inject,
  signal,
  OnDestroy,
  OnInit,
  computed,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
import {
  SelectComponent,
  SelectOption,
} from "@shared/components/ui/select/select.component";
import { SwitchComponent } from "@shared/components/ui/switch/switch.component";
import { HorusBtnDirective } from "@shared/directives/horus-btn.directive";
import { TipificationState } from "../../tipification.state";
import {
  VENDOR_OPTIONS,
  VendorOption,
} from "../../constants/tipification.constants";
import { TenantUrlUtilService } from "@shared/services/tenant-url-util.service";
import { environment } from "@env/environment";
import { Subject, takeUntil } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { StoreSelectorComponent } from "@shared/components/ui/store-selector";
import {
  TipificationDraftManager,
  TipificationDraftData,
} from "@core/utils/tipification-draft.util";
import { match, P } from "ts-pattern";
import { SearchState } from "@features/search/services/search.state";

@Component({
  selector: "app-tipification-drawer",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SelectComponent,
    SwitchComponent,
    HorusBtnDirective,
    StoreSelectorComponent,
  ],
  templateUrl: "./tipification-drawer.component.html",
  styleUrls: ["./tipification-drawer.component.scss"],
})
export class TipificationDrawerComponent implements OnInit, OnDestroy {
  private readonly dialogRef = inject(
    MatDialogRef<TipificationDrawerComponent>,
  );
  private readonly fb = inject(FormBuilder);
  readonly state = inject(TipificationState);
  private readonly searchState = inject(SearchState);
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);
  private readonly draftManager = inject(TipificationDraftManager);
  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.state.resetSubmitState();
  }

  ngOnInit(): void {
    const currentContext = {
      conversationId: this.tenantUrlUtil.conversationId(),
      participantIdCustomer: this.tenantUrlUtil.participantIdCustomer(),
      participantIdAgent: this.tenantUrlUtil.participantIdAgent(),
      typeInteraction: this.tenantUrlUtil.typeInteraction(),
      identityDocument: this.tenantUrlUtil.identityDocument(),
      queueId: this.tenantUrlUtil.queueId(),
    };
    const draft = this.draftManager.loadDraft(currentContext);
    const fallbackDraft = draft
      ? null
      : this.draftManager.loadDraftWithoutContextValidation();

    match({ draft, fallbackDraft })
      .with({ draft: P.not(P.nullish) }, ({ draft }) => {
        this.handleValidDraft(draft);
      })
      .with({ fallbackDraft: P.not(P.nullish) }, ({ fallbackDraft }) => {
        this.handleFallbackDraft(fallbackDraft, currentContext);
      })
      .otherwise(() => {
        this.handleNoDraft(currentContext);
      });
    this.isInitializing = false;
    this.setupAutoSave();
    this.setupErrorWatcher();
  }

  private handleValidDraft(draft: TipificationDraftData): void {
    this.hasPersistedSuccess.set(draft.status === "success");
    this.hasPersistedError.set(draft.status === "error");

    match(draft.formValue.tienda)
      .with("Tiendas físicas", () => {
        this.shouldUseStoreSelector.set(true);
      })
      .with("Venta Online", () => {
        this.shouldUseStoreSelector.set(false);
        if (!draft.formValue.subtienda) {
          draft.formValue.subtienda = "Venta Online";
        }
      })
      .otherwise(() => {});

    const normalizedVendor = this.normalizeVendorValue(
      draft.formValue.vendedor,
    );

    this.form.patchValue({
      ...draft.formValue,
      vendedor: normalizedVendor,
    });
    this.isGeneralInfoExpanded.set(draft.expandedStates.isGeneralInfoExpanded);
    this.isObservationsExpanded.set(
      draft.expandedStates.isObservationsExpanded,
    );
  }

  private handleFallbackDraft(
    fallbackDraft: TipificationDraftData,
    currentContext: ReturnType<typeof this.getCurrentContext>,
  ): void {
    this.draftManager.clearDraft();
    this.hasPersistedSuccess.set(false);

    const currentTenantId = this.tenantUrlUtil.currentTenant();
    if (!currentTenantId) {
      this.applyFallbackFormValues(fallbackDraft);
      return;
    }

    const tenantName = this.getTenantNameFromId(currentTenantId);
    const normalizedVendor = this.normalizeVendorValue(tenantName);
    const orderType = this.getOrderTypeFromUrl();

    match(orderType)
      .with("EC", () => {
        this.shouldUseStoreSelector.set(false);
        this.form.patchValue({
          vendedor: normalizedVendor,
          tienda: "Venta Online",
          subtienda: "Venta Online",
        });
      })
      .with("TD", () => {
        this.shouldUseStoreSelector.set(true);
        this.form.patchValue({
          vendedor: normalizedVendor,
          tienda: "Tiendas físicas",
          subtienda: null,
        });
      })
      .otherwise(() => {
        this.form.patchValue({
          vendedor: normalizedVendor,
        });
      });

    this.draftManager.saveDraft(
      this.form.value,
      {
        isGeneralInfoExpanded: this.isGeneralInfoExpanded(),
        isObservationsExpanded: this.isObservationsExpanded(),
      },
      currentContext,
    );
  }

  private handleNoDraft(
    currentContext: ReturnType<typeof this.getCurrentContext>,
  ): void {
    const currentTenantId = this.tenantUrlUtil.currentTenant();
    if (!currentTenantId) return;

    const tenantName = this.getTenantNameFromId(currentTenantId);
    const normalizedVendor = this.normalizeVendorValue(tenantName);
    const orderType = this.getOrderTypeFromUrl();

    match(orderType)
      .with("EC", () => {
        this.shouldUseStoreSelector.set(false);
        this.form.patchValue({
          vendedor: normalizedVendor,
          tienda: "Venta Online",
          subtienda: "Venta Online",
        });
      })
      .with("TD", () => {
        this.shouldUseStoreSelector.set(true);
        this.form.patchValue({
          vendedor: normalizedVendor,
          tienda: "Tiendas físicas",
          subtienda: null,
        });
      })
      .otherwise(() => {
        this.form.patchValue({
          vendedor: normalizedVendor,
        });
      });

    this.draftManager.saveDraft(
      this.form.value,
      {
        isGeneralInfoExpanded: this.isGeneralInfoExpanded(),
        isObservationsExpanded: this.isObservationsExpanded(),
      },
      currentContext,
    );
  }

  private applyFallbackFormValues(fallbackDraft: TipificationDraftData): void {
    match(fallbackDraft.formValue.tienda)
      .with("Tiendas físicas", () => {
        this.shouldUseStoreSelector.set(true);
      })
      .with("Venta Online", () => {
        this.shouldUseStoreSelector.set(false);
      })
      .otherwise(() => {});

    const normalizedVendor = this.normalizeVendorValue(
      fallbackDraft.formValue.vendedor,
    );

    this.form.patchValue({
      vendedor: normalizedVendor,
      tienda: fallbackDraft.formValue.tienda,
      subtienda:
        fallbackDraft.formValue.tienda === "Venta Online"
          ? "Venta Online"
          : fallbackDraft.formValue.subtienda,
    });
  }

  private getCurrentContext() {
    return {
      conversationId: this.tenantUrlUtil.conversationId(),
      participantIdCustomer: this.tenantUrlUtil.participantIdCustomer(),
      participantIdAgent: this.tenantUrlUtil.participantIdAgent(),
      typeInteraction: this.tenantUrlUtil.typeInteraction(),
      identityDocument: this.tenantUrlUtil.identityDocument(),
      queueId: this.tenantUrlUtil.queueId(),
    };
  }

  private getTenantNameFromId(tenantId: string): string | null {
    const tenant = environment.tenants.find((t) => t.tenantId === tenantId);
    return tenant?.tenantName ?? null;
  }

  private normalizeVendorValue(vendorName: string | null): string {
    if (!vendorName) {
      return "Sellercenter";
    }

    const validVendor = this.vendedorOptions.find(
      (option) => option.value === vendorName,
    );

    return validVendor ? vendorName : "Sellercenter";
  }

  private getOrderTypeFromUrl(): "EC" | "TD" | null {
    const docType = this.tenantUrlUtil.documentType();
    if (docType === "ORDER_ID") return "EC";
    if (docType === "SALE_NOTE_NUMBER") return "TD";
    return null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  readonly isGeneralInfoExpanded = signal(true);
  readonly isObservationsExpanded = signal(true);
  readonly hasPersistedSuccess = signal(false);
  readonly hasPersistedError = signal(false);
  private isInitializing = true;

  readonly form: FormGroup = this.fb.group({
    motivo: [null, Validators.required],
    solucionadoEnInteraccion: [true],
    incidenciaGenerada: [false],
    vendedor: [null, Validators.required],
    tienda: [null, Validators.required],
    subtienda: [null, Validators.required],
    observaciones: [""],
  });
  readonly vendedorOptions: VendorOption[] = VENDOR_OPTIONS;

  readonly tiendaOptions: SelectOption[] = [
    { value: "Venta Online", label: "Venta Online" },
    { value: "Tiendas físicas", label: "Tiendas físicas" },
  ];

  readonly subtiendaOptions: SelectOption[] = [
    { value: "Venta Online", label: "Venta Online" },
  ];

  readonly isReadOnly = computed(
    () => this.state.isSuccess() || this.hasPersistedSuccess(),
  );

  private readonly formValueSignal = toSignal(this.form.valueChanges, {
    initialValue: this.form.value,
  });

  readonly shouldUseStoreSelector = signal(false);

  readonly isFormValid = computed(() => {
    this.formValueSignal();
    return this.form.valid;
  });

  toggleGeneralInfo(): void {
    this.isGeneralInfoExpanded.update((v) => !v);
    if (!this.state.isSuccess()) {
      const context = {
        conversationId: this.tenantUrlUtil.conversationId(),
        participantIdCustomer: this.tenantUrlUtil.participantIdCustomer(),
        participantIdAgent: this.tenantUrlUtil.participantIdAgent(),
        typeInteraction: this.tenantUrlUtil.typeInteraction(),
        identityDocument: this.tenantUrlUtil.identityDocument(),
        queueId: this.tenantUrlUtil.queueId(),
      };
      this.draftManager.saveDraft(
        this.form.value,
        {
          isGeneralInfoExpanded: this.isGeneralInfoExpanded(),
          isObservationsExpanded: this.isObservationsExpanded(),
        },
        context,
      );
    }
  }

  toggleObservations(): void {
    this.isObservationsExpanded.update((v) => !v);
    if (!this.state.isSuccess()) {
      const context = {
        conversationId: this.tenantUrlUtil.conversationId(),
        participantIdCustomer: this.tenantUrlUtil.participantIdCustomer(),
        participantIdAgent: this.tenantUrlUtil.participantIdAgent(),
        typeInteraction: this.tenantUrlUtil.typeInteraction(),
        identityDocument: this.tenantUrlUtil.identityDocument(),
        queueId: this.tenantUrlUtil.queueId(),
      };
      this.draftManager.saveDraft(
        this.form.value,
        {
          isGeneralInfoExpanded: this.isGeneralInfoExpanded(),
          isObservationsExpanded: this.isObservationsExpanded(),
        },
        context,
      );
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (this.form.valid) {
      this.hasPersistedError.set(false);
      const identityDocument = this.searchState.clientDni() || this.tenantUrlUtil.identityDocument();
      const payload = this.state.buildPayload(
        this.form.value,
        identityDocument,
      );
      this.state.createTipification(payload);
      this.draftManager.markAsSuccess();
    }
  }

  closeAfterSuccess(): void {
    // Keep success state, don't reset mutation state
    this.dialogRef.close({ success: true });
  }

  handleNewTipification(): void {
    this.draftManager.clearDraft();
    this.hasPersistedSuccess.set(false);
    this.hasPersistedError.set(false);
    this.state.resetSubmitState();
    this.form.reset({
      motivo: null,
      solucionadoEnInteraccion: true,
      incidenciaGenerada: false,
      vendedor: null,
      tienda: null,
      subtienda: null,
      observaciones: "",
    });
  }

  getVendorColor(vendorValue: string): string {
    const option = this.vendedorOptions.find((o) => o.value === vendorValue);
    const tenant = environment.tenants.find(
      (t) => t.tenantName === vendorValue,
    );
    return option?.color ?? tenant?.tenantColor ?? "#CCCCCC";
  }

  getFormattedDateTime(): string {
    const now = new Date();
    const days = [
      "Domingo",
      "Lunes",
      "Martes",
      "Miércoles",
      "Jueves",
      "Viernes",
      "Sábado",
    ];
    const months = [
      "Ene",
      "Feb",
      "Mar",
      "Abr",
      "May",
      "Jun",
      "Jul",
      "Ago",
      "Sep",
      "Oct",
      "Nov",
      "Dic",
    ];

    const dayName = days[now.getDay()];
    const day = now.getDate();
    const month = months[now.getMonth()];
    const hours = now.getHours();
    const minutes = now.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "pm" : "am";
    const displayHours = hours % 12 || 12;

    return `${dayName} ${day} ${month} ${displayHours}:${minutes}${ampm}`;
  }

  private setupAutoSave(): void {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.state.isSuccess() && !this.isInitializing) {
        const context = {
          conversationId: this.tenantUrlUtil.conversationId(),
          participantIdCustomer: this.tenantUrlUtil.participantIdCustomer(),
          participantIdAgent: this.tenantUrlUtil.participantIdAgent(),
          typeInteraction: this.tenantUrlUtil.typeInteraction(),
          identityDocument: this.tenantUrlUtil.identityDocument(),
          queueId: this.tenantUrlUtil.queueId(),
        };
        this.draftManager.saveDraft(
          this.form.value,
          {
            isGeneralInfoExpanded: this.isGeneralInfoExpanded(),
            isObservationsExpanded: this.isObservationsExpanded(),
          },
          context,
        );
      }
    });

    this.form.controls["tienda"].valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        const currentSubtienda = this.form.controls["subtienda"].value;
        if (value === "Venta Online") {
          this.shouldUseStoreSelector.set(false);
          this.form.controls["subtienda"].setValue("Venta Online", {
            emitEvent: false,
          });
        } else if (value === "Tiendas físicas") {
          this.shouldUseStoreSelector.set(true);
          if (currentSubtienda === "Venta Online") {
            this.form.controls["subtienda"].setValue(null, {
              emitEvent: false,
            });
          }
        }
      });
  }

  private setupErrorWatcher(): void {
    this.state.createTipificationMutation.result$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result?.isError) {
          this.draftManager.markAsError();
          this.hasPersistedError.set(true);
        }
      });
  }
}
